import { Box } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useColors from "../../../../../../api/hooks/useColors";
import StyledDataGrid from "../../../../../../components/StyledDataGrid";
import { isComplexEntityType } from "../../../../../../utils/utils";
import EntityDrawer from "../../../../../stack/environment/components/EntityDrawer/EntityDrawer";
import { useEntities } from "../../../../../stack/environment/hooks/useEntities";
import EntityTypeTabs from "./EntityTypeTabs";
import MisconfigStatusTabs, { MisconfigStatusTab } from "./MisconfigStatusTabs";
import { getTabbedTableSX } from "./controlEntitiesTableUtils";
/*
  entitiesByTypeAndStatus structure should look like:
  {
    "host": {
      [MisconfigStatusTab.INCLUDED]: [{}, {}, ...],
      [MisconfigStatusTab.EXCLUDED]: [{}, {}, ...],
      [MisconfigStatusTab.SNOOZED]: [{}, {}, ...],
    },
    ...  
  }
  */
export default function EntityTypeAndStatusTabbedTable({
  entitiesByTypeAndStatus,
  columns,
  selectedEntityTypeTab,
  setSelectedEntityTypeTab,
  selectedStatusTab,
  setSelectedStatusTab,
  handleCheckedChange,
}) {
  const colors = useColors();
  const [searchParams, setSearchParams] = useSearchParams();
  const { flattenEntity, flattenEntities, useQueryForEntitiesByType } =
    useEntities();
  const [sortModel, setSortModel] = useState([
    { field: "affected_entity_ids", sort: "desc" },
    {
      field: "display_name",
      sort: "asc",
    },
  ]);
  const onRowClick = (row) => {
    if (isComplexEntityType(row.entity_type)) {
      const params = new URLSearchParams(searchParams);
      params.set("entity", encodeURIComponent(row.entity_id));
      setSearchParams(params.toString());
    }
  };

  const drawerEntity = entitiesByTypeAndStatus[selectedEntityTypeTab][
    selectedStatusTab
  ].find(
    (entity) =>
      entity.entity_id === decodeURIComponent(searchParams.get("entity"))
  );

  const entitiesQuery = useQueryForEntitiesByType({
    type: selectedEntityTypeTab,
    enabled: Boolean(selectedEntityTypeTab && drawerEntity),
  });

  const cachedDrawerEntity = entitiesQuery.data?.find(
    (e) => e.entity_id === drawerEntity?.entity_id
  );

  const rows = flattenEntities(
    entitiesByTypeAndStatus[selectedEntityTypeTab][selectedStatusTab]
  );

  const handleEntityTypeTabChange = (newValue) => {
    setSelectedEntityTypeTab(newValue);
    setSelectedStatusTab(MisconfigStatusTab.INCLUDED);
  };

  return (
    <Box
      sx={{
        borderRadius: "10px",
        boxShadow: "0px 0px 2px 1px rgba(203, 203, 203, 0.50);",
        backgroundColor: colors.common_white,
      }}
    >
      <Box
        sx={{
          border: "none",
          borderBottom: "1px solid " + colors.grey_20percent,
          borderRadius: "10px 10px 0 0",
          overflow: "hidden",
        }}
      >
        <EntityTypeTabs
          entitiesByTabs={entitiesByTypeAndStatus}
          selectedEntityTypeTab={selectedEntityTypeTab}
          setSelectedEntityTypeTab={handleEntityTypeTabChange}
        />
        <MisconfigStatusTabs
          entitiesByTypeAndStatus={entitiesByTypeAndStatus}
          selectedEntityTypeTab={selectedEntityTypeTab}
          selectedStatusTab={selectedStatusTab}
          setSelectedStatusTab={setSelectedStatusTab}
        />
      </Box>
      <StyledDataGrid
        boxSXProps={{
          border: "none",
          borderRadius: "0 0 10px 10px",
          paddingRight: "8px",
          ".MuiDataGrid-root": {
            border: "none",
            boxShadow: "none",
          },
        }}
        getRowId={(row) => row.entity_id}
        checkboxSelection
        disableColumnMenu
        sx={getTabbedTableSX(colors)}
        columns={columns}
        rows={rows}
        onCellClick={(params, event) => {
          if (!["__check__"].includes(params.field)) {
            event.stopPropagation();
          }
          if (
            ![
              "__check__",
              "add_to_asset",
              "affected_entity_ids",
              "actions",
            ].includes(params.field)
          ) {
            onRowClick(params.row);
          }
        }}
        onRowSelectionModelChange={handleCheckedChange}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        pageSizeOptionsOverride={[5, 10, 20, 50]}
        initialState={{
          sorting: {
            sortModel: sortModel,
          },
        }}
      />
      <EntityDrawer
        openDrawer={drawerEntity}
        selectedEntity={flattenEntity(cachedDrawerEntity || drawerEntity)}
        onClose={() => {
          const params = new URLSearchParams(searchParams);
          params.delete("entity");
          setSearchParams(params.toString());
        }}
      />
    </Box>
  );
}
